'use client';

import { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { saveAbandonedBooking } from '@/lib/abandonedBooking';
import { saveNotificationToStorage } from '@/lib/whatsappNotification';

interface BookingFormProps {
  bookingDetails: any;
  onSubmit: (formData: any) => void;
}

const BookingForm = ({ bookingDetails, onSubmit }: BookingFormProps) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequests: '',
  });
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'paypal' | 'bank' | 'tide'>('card');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [autoSaved, setAutoSaved] = useState(false);

  const getServiceFee = () => {
    switch (bookingDetails?.type) {
      case 'flight': return 15;
      case 'hotel': return 20;
      case 'holiday': return 50;
      default: return 0;
    }
  };

  const basePrice = parseFloat(bookingDetails?.price || 0);
  const serviceFee = getServiceFee();
  const totalPrice = basePrice + serviceFee;

  // Auto-save form data as abandoned booking
  const autoSaveBooking = useCallback(() => {
    const hasData = formData.firstName || formData.email || formData.phone;
    if (!hasData) return;
    saveAbandonedBooking({
      step: 'form',
      formData,
      bookingDetails,
      bookingType: bookingDetails?.type || 'flight',
    });
    setAutoSaved(true);
    setTimeout(() => setAutoSaved(false), 2000);
  }, [formData, bookingDetails]);

  // Auto-save every 30 seconds if form has data
  useEffect(() => {
    const interval = setInterval(autoSaveBooking, 30000);
    return () => clearInterval(interval);
  }, [autoSaveBooking]);

  // Save on blur of important fields
  const handleFieldBlur = () => {
    const hasData = formData.firstName || formData.email || formData.phone;
    if (hasData) autoSaveBooking();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToTerms) {
      alert('Please agree to the terms and conditions');
      return;
    }
    // Save notification to localStorage
    saveNotificationToStorage({
      type: 'lead_received',
      name: `${formData.firstName} ${formData.lastName}`.trim(),
      phone: formData.phone,
      email: formData.email,
      destination: bookingDetails?.to || bookingDetails?.destination,
    });
    onSubmit({ ...formData, paymentMethod, totalPrice });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Auto-save indicator */}
      {autoSaved && (
        <div className="flex items-center gap-2 text-xs text-trust-green bg-trust-green/10 px-3 py-2 rounded-lg">
          <Icon name="CheckCircleIcon" size={14} />
          Progress saved automatically
        </div>
      )}

      {/* Personal Information */}
      <div className="bg-card rounded-xl shadow-brand p-5">
        <h3 className="font-heading font-bold text-lg text-foreground mb-4 flex items-center gap-2">
          <Icon name="UserIcon" size={22} />
          Personal Information
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-bold text-foreground mb-2">First Name *</label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              onBlur={handleFieldBlur}
              className="w-full px-4 py-3.5 text-base border-2 border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent touch-manipulation"
              required
              autoComplete="given-name"
              inputMode="text"
            />
          </div>
          
          <div>
            <label className="block text-sm font-bold text-foreground mb-2">Last Name *</label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              onBlur={handleFieldBlur}
              className="w-full px-4 py-3.5 text-base border-2 border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent touch-manipulation"
              required
              autoComplete="family-name"
              inputMode="text"
            />
          </div>
          
          <div>
            <label className="block text-sm font-bold text-foreground mb-2">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              onBlur={handleFieldBlur}
              className="w-full px-4 py-3.5 text-base border-2 border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent touch-manipulation"
              required
              autoComplete="email"
              inputMode="email"
            />
          </div>
          
          <div>
            <label className="block text-sm font-bold text-foreground mb-2">Phone *</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              onBlur={handleFieldBlur}
              className="w-full px-4 py-3.5 text-base border-2 border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent touch-manipulation"
              required
              autoComplete="tel"
              inputMode="tel"
            />
          </div>
        </div>
        
        <div className="mt-4">
          <label className="block text-sm font-bold text-foreground mb-2">Special Requests (Optional)</label>
          <textarea
            value={formData.specialRequests}
            onChange={(e) => setFormData({ ...formData, specialRequests: e.target.value })}
            rows={3}
            className="w-full px-4 py-3.5 text-base border-2 border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent touch-manipulation resize-none"
            placeholder="Any special requirements or requests..."
          />
        </div>
      </div>

      {/* Payment Method */}
      <div className="bg-card rounded-xl shadow-brand p-5">
        <h3 className="font-heading font-bold text-lg text-foreground mb-4 flex items-center gap-2">
          <Icon name="CreditCardIcon" size={22} />
          Payment Method
        </h3>
        
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {(['card', 'paypal', 'bank', 'tide'] as const).map((method) => (
            <button
              key={method}
              type="button"
              onClick={() => setPaymentMethod(method)}
              className={`p-4 border-2 rounded-xl transition-all touch-manipulation min-h-[80px] flex flex-col items-center justify-center gap-2 ${
                paymentMethod === method
                  ? 'border-primary bg-primary/10 shadow-md'
                  : 'border-border hover:border-primary/50 active:bg-gray-50'
              }`}
            >
              {method === 'tide' ? (
                <div className="w-7 h-7 bg-[#00B4D8] rounded-full flex items-center justify-center">
                  <span className="text-white font-black text-xs">T</span>
                </div>
              ) : (
                <Icon name={method === 'bank' ? 'BuildingLibraryIcon' : 'CreditCardIcon'} size={28} className={paymentMethod === method ? 'text-primary' : 'text-muted-foreground'} />
              )}
              <p className={`font-bold text-xs text-center leading-tight ${paymentMethod === method ? 'text-primary' : 'text-foreground'}`}>
                {method === 'card' ? 'Card' : method === 'paypal' ? 'PayPal' : method === 'bank' ? 'Bank' : 'Tide Pay'}
              </p>
            </button>
          ))}
        </div>

        {/* Tide Payment Section */}
        {paymentMethod === 'tide' && (
          <div className="bg-[#00B4D8]/10 border-2 border-[#00B4D8]/30 rounded-xl p-5 mb-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-[#00B4D8] rounded-full flex items-center justify-center">
                <span className="text-white font-black text-sm">T</span>
              </div>
              <h4 className="font-heading font-black text-foreground">Pay via Tide</h4>
              <span className="ml-auto text-xs bg-green-100 text-green-700 font-bold px-2 py-1 rounded-full">Secure</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Scan the QR code or click the payment link to complete your payment securely via Tide.</p>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              {/* QR Code Image */}
              <div className="flex-shrink-0">
                <div className="bg-white p-3 rounded-xl shadow-md border border-border">
                  <img
                    src="/assets/images/WhatsApp_Image_2026-03-01_at_7.24.40_AM-1772332114229.jpeg"
                    alt="Tide payment QR code - scan to pay"
                    className="w-40 h-40 object-contain"
                  />
                </div>
                <p className="text-xs text-center text-muted-foreground mt-2 font-bold">Scan to Pay</p>
              </div>
              <div className="flex-1 space-y-3">
                <div className="bg-white rounded-lg p-3 border border-border">
                  <p className="text-xs text-muted-foreground mb-1">Payment Link</p>
                  <a
                    href="https://pay.tide.co/pay/93db3839-9be1-4d9c-adec-feac965f8aac"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#00B4D8] font-bold text-sm break-all hover:underline"
                  >
                    pay.tide.co/pay/93db3839...
                  </a>
                </div>
                <a
                  href="https://pay.tide.co/pay/93db3839-9be1-4d9c-adec-feac965f8aac"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full bg-[#00B4D8] text-white font-bold py-3 rounded-xl hover:bg-[#0096B4] transition-colors"
                >
                  <Icon name="ArrowTopRightOnSquareIcon" size={18} />
                  Open Tide Payment Page
                </a>
                <p className="text-xs text-muted-foreground text-center">
                  After payment, click &quot;Proceed to Payment&quot; below to confirm your booking
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Price Breakdown */}
        <div className="bg-surface rounded-xl p-4 space-y-2">
          <div className="flex justify-between text-foreground text-sm">
            <span>Base Price:</span>
            <span className="font-bold">£{basePrice.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-foreground text-sm">
            <span>Service Fee:</span>
            <span className="font-bold">£{serviceFee.toFixed(2)}</span>
          </div>
          <div className="border-t border-border pt-2 flex justify-between text-lg font-bold text-primary">
            <span>Total:</span>
            <span>£{totalPrice.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Terms */}
      <div className="bg-card rounded-xl shadow-brand p-5">
        <label className="flex items-start gap-3 cursor-pointer touch-manipulation">
          <div className="relative mt-0.5">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="sr-only"
              required
            />
            <div
              onClick={() => setAgreedToTerms(!agreedToTerms)}
              className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all cursor-pointer ${
                agreedToTerms ? 'bg-primary border-primary' : 'border-border bg-white'
              }`}
            >
              {agreedToTerms && <Icon name="CheckIcon" size={14} className="text-white" />}
            </div>
          </div>
          <span className="text-sm text-foreground leading-relaxed">
            I agree to the{' '}
            <a href="/terms-and-conditions" className="text-primary hover:underline font-bold">Terms and Conditions</a>
            {' '}and{' '}
            <a href="/privacy-policy" className="text-primary hover:underline font-bold">Privacy Policy</a>.
            A service fee of £{serviceFee} will be charged.
          </span>
        </label>
      </div>

      {/* Submit Button - large touch target */}
      <button
        type="submit"
        disabled={!agreedToTerms}
        className="w-full bg-gradient-to-r from-primary to-secondary text-white font-heading font-black px-8 py-5 rounded-xl hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg touch-manipulation active:scale-95"
      >
        <Icon name="CheckCircleIcon" size={24} />
        <span>Proceed to Payment — £{totalPrice.toFixed(2)}</span>
      </button>
      
      {/* WhatsApp alternative */}
      <div className="text-center">
        <p className="text-sm text-muted-foreground mb-2">Prefer to book via WhatsApp?</p>
        <a
          href={`https://wa.me/442046152876?text=${encodeURIComponent(`Hi, I'd like to book a ${bookingDetails?.type || 'trip'} to ${bookingDetails?.to || bookingDetails?.destination || 'my destination'}. Please help me complete my booking.`)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-5 py-3 bg-green-500 text-white font-bold rounded-xl hover:bg-green-600 transition-all touch-manipulation"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          Book via WhatsApp
        </a>
      </div>

      <p className="text-center text-sm text-muted-foreground">
        <Icon name="ShieldCheckIcon" size={16} className="inline mr-1" />
        Secure payment processing. Your information is protected.
      </p>
    </form>
  );
};

export default BookingForm;
